import keywordImpressionsService from "@/services/keywordImpressions";
import { pickBy } from "lodash";
import { cancelRequests, hasPendingRequest, loadLatest } from "./requests";

function prepareFilters(filters) {
  return pickBy({
    gmb_address_ids: filters.gmb_address_ids.join(","),
    cities: filters.cities.join(","),
    facilities_groups_ids: filters.facilities_groups_ids.join(","),
    date_from: filters.date_from,
    date_to: filters.date_to,
  });
}

export default {
  fetchKeywordsTableData(context, { search } = {}) {
    const { commit, state } = context;
    const payload = {
      search,
      ordering: state.ordering,
      page: state.pagination.page,
      page_size: state.pagination.pageSize,
      ...prepareFilters(state.filters),
    };
    return loadLatest(
      context,
      "list",
      (signal) =>
        keywordImpressionsService.fetchKeywords({ ...payload, signal }),
      (response) => {
        commit(
          "SET_KEYWORDS_TABLE_DATA",
          response || { results: [], count: 0 }
        );
        const stats = response && response.search_stats;
        commit("SET_SEARCH_STATS", {
          keywordsCount: stats ? stats.keywords_count || 0 : 0,
          totalImpressions: stats ? stats.total_impressions || 0 : 0,
        });
      }
    );
  },

  fetchSummary(context, { search } = {}) {
    const payload = { search, ...prepareFilters(context.state.filters) };
    return loadLatest(
      context,
      "summary",
      (signal) => keywordImpressionsService.fetchSummary(payload, { signal }),
      (response) => context.commit("SET_SUMMARY", response || null)
    );
  },

  async fetchTopKeywordsSummary(context, { search } = {}) {
    const { commit, state } = context;
    const payload = { search, ...prepareFilters(state.filters) };
    const seedKeywords =
      !search && !state.keywordSearch && state.keywords.length === 0;
    let applied = false;
    await loadLatest(
      context,
      "topKeywords",
      async (signal) => {
        const response =
          await keywordImpressionsService.fetchTopKeywordsSummary(
            { ...payload, include_overview: true },
            { signal }
          );
        if (!response.data || signal.aborted) return response;
        const data = { ...response.data };
        // Keep compatibility while backend and frontend are deployed separately.
        const fallbacks = [];
        if (!data.summary) {
          fallbacks.push(
            keywordImpressionsService
              .fetchSummary(payload, { signal })
              .then((summary) => {
                data.summary = summary.data;
              })
          );
        }
        if (seedKeywords && !data.keyword_options && !signal.aborted) {
          fallbacks.push(
            keywordImpressionsService
              .fetchTopKeywords({ ...payload, signal })
              .then((options) => {
                data.keyword_options = options.data;
              })
          );
        }
        await Promise.all(fallbacks);
        return { ...response, data };
      },
      (response) => {
        applied = Boolean(response);
        commit("SET_TOP_KEYWORDS", response || null);
        commit("SET_SUMMARY", (response && response.summary) || null);
        if (
          seedKeywords &&
          !state.keywordSearch &&
          !hasPendingRequest(state, "keywords") &&
          state.keywords.length === 0
        ) {
          commit(
            "SET_KEYWORDS",
            (response && response.keyword_options) || { results: [] }
          );
        }
      },
      ["topKeywords", "summary"]
    );
    // Searching can cancel the initial overview before it seeds the independent chart selector.
    if (
      applied &&
      search &&
      !state.keywordSearch &&
      state.keywords.length === 0 &&
      !hasPendingRequest(state, "keywords")
    ) {
      await context.dispatch("fetchKeywords");
    }
  },

  fetchKeywords(context, { search } = {}) {
    const { commit, state } = context;
    commit("SET_KEYWORD_SEARCH", search || "");
    commit("SET_LOADING_MORE_KEYWORDS", false);
    const payload = {
      search: search || undefined,
      ...prepareFilters(state.filters),
    };
    return loadLatest(
      context,
      "keywords",
      (signal) =>
        keywordImpressionsService.fetchTopKeywords({ ...payload, signal }),
      (response) => commit("SET_KEYWORDS", response || { results: [] })
    );
  },

  async fetchMoreKeywords(context) {
    const { commit, state } = context;
    if (
      !state.keywordsNextPage ||
      state.loadingMoreKeywords ||
      state.loading.keywords
    )
      return;
    const page = new URL(state.keywordsNextPage).searchParams.get("page");
    const payload = {
      ...prepareFilters(state.filters),
      search: state.keywordSearch || undefined,
      page,
    };
    commit("SET_LOADING_MORE_KEYWORDS", true);
    try {
      await loadLatest(
        context,
        "keywords",
        (signal) =>
          keywordImpressionsService.fetchTopKeywords({ ...payload, signal }),
        (response) => commit("APPEND_KEYWORDS", response || { results: [] })
      );
    } finally {
      if (!hasPendingRequest(state, "keywords"))
        commit("SET_LOADING_MORE_KEYWORDS", false);
    }
  },

  fetchKeywordByDate(context, { keyword } = {}) {
    const payload = { keyword, ...prepareFilters(context.state.filters) };
    return loadLatest(
      context,
      "byDate",
      (signal) =>
        keywordImpressionsService.fetchKeywordByDate(payload, { signal }),
      (response) => context.commit("SET_KEYWORD_BY_DATE", response || [])
    );
  },

  updatePagination({ commit }, { page, pageSize }) {
    commit("SET_PAGINATION", { page, pageSize });
  },

  updateOrdering({ commit }, ordering) {
    commit("SET_ORDERING", ordering);
  },

  updateFilters({ commit, state }, filters) {
    cancelRequests(state);
    commit("SET_FILTERS", filters);
  },

  resetData({ commit, state }) {
    cancelRequests(state);
    commit("RESET_DATA");
  },
};
